import { useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import type { ModelViewerTextureUpdateSignal } from './TextureUpdateSignal'
import { renderReadiness } from '../Rendering/browserReadiness'

interface Props {
    signal: ModelViewerTextureUpdateSignal
}

/**
 * Invalidates the demand renderer directly after every composite. Interactive
 * updates need one frame; renderbot revisions retain their two-frame barrier so
 * the uploaded CanvasTexture is visible before Puppeteer captures the canvas.
 */
export default function TextureReady(props: Props) {
    const invalidate = useThree(state => state.invalidate)
    const renderFrame = useRef<number>()
    const readyFrame = useRef<number>()

    useEffect(() => {
        const cancelPendingFrames = () => {
            if(renderFrame.current !== undefined) {
                cancelAnimationFrame(renderFrame.current)
                renderFrame.current = undefined
            }
            if(readyFrame.current !== undefined) {
                cancelAnimationFrame(readyFrame.current)
                readyFrame.current = undefined
            }
        }

        const unsubscribe = props.signal.subscribe(update => {
            cancelPendingFrames()
            invalidate()

            const revision = update.revision
            if(revision === undefined) return
            renderReadiness.reset('texture', revision)

            renderFrame.current = requestAnimationFrame(() => {
                renderFrame.current = undefined
                invalidate()
                readyFrame.current = requestAnimationFrame(() => {
                    readyFrame.current = undefined
                    renderReadiness.acknowledge(
                        'texture',
                        revision,
                    )
                })
            })
        })

        return () => {
            unsubscribe()
            cancelPendingFrames()
        }
    }, [invalidate, props.signal])

    return null
}
